import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "I'm Xadentra's AI assistant. Ask me anything about Adobe Marketo Engage, Salesforce sync, lead routing, lifecycle programs, or reporting. If you'd like a strategy call, I can point you to the right next step.",
    },
  ]);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const chunk of parts) {
          const lines = chunk.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) {
              const payload = line.slice(5);
              data += payload.startsWith(" ") ? payload.slice(1) : payload;
            }
          }
          if (event === "done") {
            setSessionId(data.trim() || sessionId);
          } else if (event === "error") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: "Sorry — something went wrong. Please try again." };
              return copy;
            });
          } else {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, content: last.content + data };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection error. Try again in a moment." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-30 rounded-full p-3 sm:p-4 bg-gradient-to-br from-[#6D4DFF] to-[#00D4FF] text-white shadow-[0_12px_36px_-12px_rgba(109,77,255,0.6)] hover:shadow-[0_18px_48px_-12px_rgba(0,212,255,0.55)] transition-all"
        aria-label="Open chat"
      >
        {open ? <X className="w-4 h-4 sm:w-5 sm:h-5" /> : <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-30 w-[calc(100vw-2rem)] sm:w-[92vw] max-w-md bg-[#07111F] border border-white/10 rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.55)] flex flex-col overflow-hidden xa-fade-up"
          style={{ height: "min(560px, 75vh)" }}
        >
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#6D4DFF]/15 to-[#00D4FF]/15">
            <div>
              <div className="font-display font-semibold text-base text-white">Xadentra AI</div>
              <div className="text-xs text-white/55">Senior MOPs perspective, on-demand</div>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#3EE8A5]">● Online</span>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 space-y-3 xa-scroll">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[88%] text-sm leading-relaxed rounded-xl ${
                  m.role === "user"
                    ? "ml-auto bg-gradient-to-br from-[#6D4DFF] to-[#5938E6] text-white px-4 py-3"
                    : "bg-white/5 border border-white/10 text-white/95 px-4 py-3"
                }`}
                data-testid={`chat-message-${m.role}-${i}`}
              >
                {m.content || (
                  <span className="inline-flex items-center gap-2 text-white/55">
                    <Loader2 className="w-3 h-3 animate-spin" /> thinking…
                  </span>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={send} className="border-t border-white/10 flex bg-white/5">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Adobe Marketo Engage, SFDC, attribution…"
              className="flex-1 px-4 py-3 text-sm bg-transparent text-white placeholder:text-white/35 focus:outline-none"
              disabled={busy}
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={busy || !input.trim()}
              className="px-4 bg-[#6D4DFF] text-white hover:bg-[#5938E6] disabled:opacity-50 transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
